import React, { useContext, useRef, useEffect, useState } from 'react';
import DndAnswerContext from 'components/common/Context';
import cn from '@/utils/cn';
import styles from './DropZoneArea.module.scss';
import { backgroundStyle } from '../../utils';
import DropZoneWithDraggableChoices from './DropZoneWithDraggableChoices';
import TextArea from '../TextArea';
import { CorrectAnswersType, DraggableContainerPosition } from '../../types';
import { DIMENSIONS } from '../index';

interface DropZoneAreaProps {
  inBuilder?: boolean;
  correctChoices?: CorrectAnswersType;
  cumulativeDndScalar: number;
  isComplete?: boolean;
  isDragDisabled?: boolean;
  draggableContainerPosition: DraggableContainerPosition;
}
const DropZoneArea = ({
  inBuilder = false,
  correctChoices,
  cumulativeDndScalar,
  isComplete = false,
  isDragDisabled = false,
  draggableContainerPosition,
}: DropZoneAreaProps) => {
  const backgroundRef = useRef();
  const [wrapperStyles, setWrapperStyles] = useState({});
  const [wrapperHeight, setWrapperHeight] = useState(0);
  const {
    answerChoices,
    background,
    draggableChoices,
    dropZones,
    showAnswerKey,
    textAreas,
    studentId,
  } = useContext(DndAnswerContext);
  const isHorizontallyPositioned = [
    DraggableContainerPosition.Top, DraggableContainerPosition.Bottom
  ].includes(draggableContainerPosition);

  /**
   * If the question is horizontally positioned, this useEffect calculates the new width:
   *
   * - Multiply the current height by the aspect ratio of the question container
   * - Account for the two CSS transform scalings that are done on DnD questions (cumulativeDndScalar prop)
   * - Multiply by 2/3: which is the flex ratio of:
   *   - this component (flex: 2) to:
   *   - DraggableChoicesBank (flex: 1)
   */
  useEffect(() => {
    if (!backgroundRef.current || !isHorizontallyPositioned) return;

    // @ts-ignore
    const sizes = backgroundRef.current?.getBoundingClientRect();
    const aspectRatio = DIMENSIONS.width / DIMENSIONS.height;
    const width = sizes.height * aspectRatio / cumulativeDndScalar * 2 / 3;
    setWrapperHeight(sizes.height / cumulativeDndScalar);
    setWrapperStyles({ width: `${width}px` });
  }, [backgroundRef, backgroundRef.current, setWrapperHeight, setWrapperStyles, isHorizontallyPositioned])

  return (
    <>
      {isHorizontallyPositioned && (
        <div
          className={cn(styles.whiteBackground, {
            'tw-bottom-0': draggableContainerPosition === DraggableContainerPosition.Top,
            'tw-top-0': draggableContainerPosition === DraggableContainerPosition.Bottom,
          })}
          style={{ ...backgroundStyle(background), backgroundRepeat: 'no-repeat', height: wrapperHeight }}
        />
      )}
      <div
        ref={backgroundRef}
        className={cn(styles.dropZoneArea, {
          [styles.horizontalDropZoneArea]: isHorizontallyPositioned,
        })}
        style={{ ...(!isHorizontallyPositioned && backgroundStyle(background)), ...wrapperStyles }}
      >
        <div className={styles.posAbsolute}>
          {Object.keys(dropZones).sort().map((key) => {
            const shouldUseCorrectChoices = !!(!studentId && showAnswerKey && correctChoices[key]);

            return (
              <DropZoneWithDraggableChoices
                key={key}
                answerChoices={shouldUseCorrectChoices ? correctChoices : answerChoices}
                draggableChoices={draggableChoices}
                dropZones={dropZones}
                id={key}
                isAnswerKeyView={shouldUseCorrectChoices}
                isComplete={isComplete}
                isCorrect={shouldUseCorrectChoices}
                isDragDisabled={isDragDisabled}
              />
            );
          })}
          {Object.keys(textAreas).sort().map(key => (
            <TextArea
              key={key}
              inBuilder={inBuilder}
              id={key}
              {...textAreas[key]}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default DropZoneArea;
